import { colors, sentenceCase } from '@cabonline/ui-shared';
import {
  Close,
  IconEye,
  IconEyeInvisible,
  IconWarning,
} from '@cabonline/ui-shared';
import {
  InputHTMLAttributes,
  ReactNode,
  forwardRef,
  useRef,
  useState,
} from 'react';

import { Label } from '../label';
import {
  Container,
  ErrorText,
  ErrorWrap,
  HelpText,
  InputWrapper,
  PressableIconWrap,
  StyledRequired,
  StyledTextInput,
} from './styles';

export type InputProps = {
  hasError?: boolean | string;
  label?: string;
  help?: string;
  clearable?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  onIconPress?: () => void;
  isRequired?: boolean;
  testID?: string;
  id: string;
  datePickerInputId?: string;
  ariaLabels?: {
    showPassword?: string;
    hidePassword?: string;
    clearInput?: string;
    iconAriaLabel?: string;
  };
  sentencedCased?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    id,
    datePickerInputId,
    type = 'text',
    hasError,
    label,
    help,
    clearable,
    onClear,
    icon,
    onIconPress,
    isRequired,
    disabled,
    inputMode,
    value,
    sentencedCased = false,
    ariaLabels: { hidePassword, showPassword, clearInput, iconAriaLabel } = {},
    ...rest
  } = props;
  const [secureText, setSecureText] = useState(type === 'password');

  const innerRef = useRef<HTMLInputElement>(null);

  return (
    <Container>
      {label && (
        <Label
          htmlFor={id}
          id={`inputLabel-${datePickerInputId ? datePickerInputId : id}`}
        >
          {label}
          {isRequired && <StyledRequired aria-hidden> *</StyledRequired>}
        </Label>
      )}
      {help && <HelpText>{help}</HelpText>}
      {hasError && typeof hasError === 'string' && (
        <ErrorWrap
          data-testid={rest.testID ? `${rest.testID}-error` : undefined}
        >
          <IconWarning color={colors.red} />
          <ErrorText as="span">{hasError}</ErrorText>
        </ErrorWrap>
      )}
      <InputWrapper>
        <StyledTextInput
          ref={ref || innerRef}
          id={datePickerInputId ? datePickerInputId : id}
          data-testid={rest.testID}
          inputMode={inputMode}
          type={type === 'password' && !secureText ? 'text' : type}
          disabled={disabled}
          hasError={Boolean(hasError)}
          aria-required={isRequired}
          onClick={() => {
            !disabled && innerRef?.current?.focus();
          }}
          aria-labelledby={`inputLabel-${datePickerInputId ?? id}`}
          value={sentencedCased ? sentenceCase(value?.toString()) : value}
          {...rest}
        />
        {type === 'password' && (
          <PressableIconWrap
            type="button"
            onClick={() => setSecureText(!secureText)}
            title={secureText ? showPassword : hidePassword}
            aria-label={secureText ? showPassword : hidePassword}
          >
            {secureText ? <IconEyeInvisible /> : <IconEye />}
          </PressableIconWrap>
        )}
        {clearable && (
          <PressableIconWrap
            type="button"
            onClick={onClear}
            title={clearInput}
            aria-label={clearInput}
          >
            <Close />
          </PressableIconWrap>
        )}
        {icon && (
          <PressableIconWrap
            type="button"
            onClick={onIconPress}
            title={iconAriaLabel}
            aria-label={iconAriaLabel}
          >
            {icon}
          </PressableIconWrap>
        )}
      </InputWrapper>
    </Container>
  );
});
